/**
 * Default configs — a playable activity for any kind, at any level, on any theme.
 *
 * Two jobs. It gives the Studio a real activity from one tap, and it gives the
 * agent a worked example to edit instead of a blank page, which is what keeps
 * generated specs valid.
 *
 * Every factory ends in a `parse`, so a broken ladder throws here rather than
 * reaching a child. Everything is a pure function of (kind, difficulty, theme).
 */

import type { InteractiveKind, Level, SkillId } from '../types'
import type { InteractiveSpecBase } from '../types'

import { KIND_LABELS, byLevel, cycle } from './common'
import {
  CLASSIC_TANGRAM_PIECES,
  READING_PASSAGES,
  SIGHT_WORDS,
  TANGRAM_SQUARE_OUTLINE,
  TANGRAM_TRAY_OFFSETS,
  countWords,
} from './content'
import { buildDeductionPuzzle } from './deduction'
import { LOWERCASE_FORMATION_ORDER, UPPERCASE_ORDER, strokeTemplate } from './strokes'
import { type ThemeItem, type ThemePack, resolveThemePack } from './themes'

import { type TangramConfig, TangramConfigSchema } from './kinds/tangram'
import {
  MATRIX_SHAPES,
  MATRIX_SIZES,
  type MatrixCellContent,
  type MatrixDimension,
  type MatrixReasoningConfig,
  MatrixReasoningConfigSchema,
} from './kinds/matrix-reasoning'
import { type SightWordDeckConfig, SightWordDeckConfigSchema } from './kinds/sight-word-deck'
import { type CountingGameConfig, CountingGameConfigSchema } from './kinds/counting-game'
import { type PatternBuilderConfig, PatternBuilderConfigSchema } from './kinds/pattern-builder'
import { type DeductionPuzzleConfig, DeductionPuzzleConfigSchema } from './kinds/deduction-puzzle'
import { type SortingGameConfig, SortingGameConfigSchema } from './kinds/sorting-game'
import { type SequenceOrderConfig, SequenceOrderConfigSchema } from './kinds/sequence-order'
import { type LetterTracingConfig, LetterTracingConfigSchema } from './kinds/letter-tracing'
import { type ReadingTimerConfig, ReadingTimerConfigSchema } from './kinds/reading-timer'
import { type MemoryPairsConfig, MemoryPairsConfigSchema } from './kinds/memory-pairs'
import { type Model3dConfig, Model3dConfigSchema } from './kinds/model-3d'
import { type StoryBuilderConfig, StoryBuilderConfigSchema } from './kinds/story-builder'

/** The config type for each kind, so `defaultConfigFor('tangram', 3)` types precisely. */
export interface ConfigByKind {
  tangram: TangramConfig
  matrix_reasoning: MatrixReasoningConfig
  sight_word_deck: SightWordDeckConfig
  counting_game: CountingGameConfig
  pattern_builder: PatternBuilderConfig
  deduction_puzzle: DeductionPuzzleConfig
  sorting_game: SortingGameConfig
  sequence_order: SequenceOrderConfig
  letter_tracing: LetterTracingConfig
  reading_timer: ReadingTimerConfig
  memory_pairs: MemoryPairsConfig
  model_3d: Model3dConfig
  story_builder: StoryBuilderConfig
}

// ─────────────────────────────────────────────────────────────
// tangram
// ─────────────────────────────────────────────────────────────

function tangramConfig(difficulty: Level): TangramConfig {
  const plan = byLevel(difficulty, [
    { placed: 5, rotate: false, tolerance: 0.14, guide: 'segmented', flip: false, snap: 90 },
    { placed: 4, rotate: false, tolerance: 0.12, guide: 'segmented', flip: false, snap: 90 },
    { placed: 2, rotate: true, tolerance: 0.1, guide: 'segmented', flip: false, snap: 90 },
    { placed: 0, rotate: true, tolerance: 0.08, guide: 'segmented', flip: false, snap: 45 },
    { placed: 0, rotate: true, tolerance: 0.06, guide: 'silhouette', flip: true, snap: 45 },
  ] as const)

  return TangramConfigSchema.parse({
    pieceSet: 'classic7',
    pieces: CLASSIC_TANGRAM_PIECES.map((piece) => {
      const tray = TANGRAM_TRAY_OFFSETS[piece.id]
      return {
        id: piece.id,
        shape: piece.shape,
        colorToken: piece.colorToken,
        polygon: piece.polygon,
        start: {
          dx: tray.dx,
          dy: tray.dy,
          // With rotation switched off a rotated tray piece could never be solved.
          rotationDeg: plan.rotate ? tray.rotationDeg : 0,
          flipped: false,
        },
      }
    }),
    targetOutline: TANGRAM_SQUARE_OUTLINE,
    targetLabel: 'a big square',
    prePlacedPieceIds: CLASSIC_TANGRAM_PIECES.slice(0, plan.placed).map((piece) => piece.id),
    snapTolerance: plan.tolerance,
    rotationAllowed: plan.rotate,
    rotationSnapDegrees: plan.snap,
    flipAllowed: plan.flip,
    guideMode: plan.guide,
  })
}

// ─────────────────────────────────────────────────────────────
// matrix_reasoning
// ─────────────────────────────────────────────────────────────

const MATRIX_POOLS = {
  shape: [...MATRIX_SHAPES],
  color: ['piece.coral', 'piece.teal', 'piece.amber', 'piece.violet'],
  size: [...MATRIX_SIZES],
  count: [1, 2, 3],
  rotation: [0, 90, 180],
} as const

function setDimension(
  content: MatrixCellContent,
  dimension: MatrixDimension,
  index: number,
): MatrixCellContent {
  switch (dimension) {
    case 'shape':
      return { ...content, shape: cycle(MATRIX_POOLS.shape, index) }
    case 'color':
      return { ...content, colorToken: cycle(MATRIX_POOLS.color, index) }
    case 'size':
      return { ...content, size: cycle(MATRIX_POOLS.size, index) }
    case 'count':
      return { ...content, count: cycle(MATRIX_POOLS.count, index) }
    case 'rotation':
      return { ...content, rotationDeg: cycle(MATRIX_POOLS.rotation, index) }
  }
}

function matrixKey(content: MatrixCellContent): string {
  return [content.shape, content.colorToken, content.size, content.count, content.rotationDeg].join('|')
}

function matrixConfig(difficulty: Level, pack: ThemePack): MatrixReasoningConfig {
  const plan = byLevel(difficulty, [
    { side: 2, dims: ['count'], emoji: true },
    { side: 2, dims: ['color'], emoji: false },
    { side: 2, dims: ['shape', 'color'], emoji: false },
    { side: 3, dims: ['color'], emoji: false },
    { side: 3, dims: ['shape', 'color'], emoji: false },
  ] as const) as { side: 2 | 3; dims: readonly MatrixDimension[]; emoji: boolean }

  // A dimension the emoji would hide is not a dimension she can read.
  const emoji = plan.emoji ? pack.objectEmoji : undefined
  const base: MatrixCellContent = {
    shape: 'circle',
    colorToken: 'piece.teal',
    size: 'medium',
    count: 1,
    rotationDeg: 0,
    ...(emoji ? { emoji } : {}),
  }

  // The first varying dimension reads across the row, the second down the column.
  const contentAt = (row: number, column: number): MatrixCellContent => {
    let content = setDimension(base, plan.dims[0], column)
    if (plan.dims[1]) content = setDimension(content, plan.dims[1], row)
    return content
  }

  const cells = []
  for (let row = 0; row < plan.side; row++) {
    for (let column = 0; column < plan.side; column++) {
      const last = row === plan.side - 1 && column === plan.side - 1
      cells.push({ ...contentAt(row, column), blank: last })
    }
  }

  const answer = contentAt(plan.side - 1, plan.side - 1)
  const wanted = difficulty >= 4 ? 4 : 3
  const distractors: MatrixCellContent[] = []
  const seen = new Set([matrixKey(answer)])
  // Near misses first: one dimension off, so choosing needs the rule, not a glance.
  const dimensionOrder: MatrixDimension[] = [...plan.dims, 'count', 'size', 'shape', 'color']
  for (const dimension of dimensionOrder) {
    for (let index = 0; index < 4 && distractors.length < wanted - 1; index++) {
      const variant = setDimension(answer, dimension, index)
      const key = matrixKey(variant)
      if (seen.has(key)) continue
      seen.add(key)
      distractors.push(variant)
    }
    if (distractors.length >= wanted - 1) break
  }

  const correctIndex = (plan.side + plan.dims.length) % wanted
  const options = [...distractors]
  options.splice(correctIndex, 0, answer)

  const readAcross = plan.dims[0]
  const readDown = plan.dims[1]
  return MatrixReasoningConfigSchema.parse({
    gridSize: plan.side === 2 ? '2x2' : '3x3',
    varyingDimensions: plan.dims,
    cells,
    options,
    correctIndex,
    ruleHint: readDown
      ? `The ${readAcross} changes as you read across; the ${readDown} changes as you go down.`
      : `The ${readAcross} changes as you read across, and every row does the same thing.`,
  })
}

// ─────────────────────────────────────────────────────────────
// sight_word_deck
// ─────────────────────────────────────────────────────────────

function sightWordConfig(difficulty: Level): SightWordDeckConfig {
  const plan = byLevel(difficulty, [
    { words: 4, flash: 4_000, instant: false, threshold: 3_000, ordering: 'fixed', passes: 1 },
    { words: 6, flash: 3_000, instant: false, threshold: 2_500, ordering: 'fixed', passes: 1 },
    { words: 8, flash: 2_500, instant: true, threshold: 2_000, ordering: 'shuffled', passes: 1 },
    { words: 10, flash: 2_000, instant: true, threshold: 1_500, ordering: 'shuffled', passes: 2 },
    { words: 12, flash: 1_500, instant: true, threshold: 1_200, ordering: 'shuffled', passes: 2 },
  ] as const)

  return SightWordDeckConfigSchema.parse({
    words: SIGHT_WORDS.slice(0, plan.words).map((text, index) => ({
      text,
      status: index < plan.words / 2 ? 'practicing' : 'new',
    })),
    flashDurationMs: plan.flash,
    requireInstantRecognition: plan.instant,
    instantThresholdMs: plan.threshold,
    ordering: plan.ordering,
    shuffleSeed: 1_000 + difficulty,
    echoPrompt: difficulty <= 2,
    passes: plan.passes,
  })
}

// ─────────────────────────────────────────────────────────────
// counting_game
// ─────────────────────────────────────────────────────────────

function countingConfig(difficulty: Level, pack: ThemePack): CountingGameConfig {
  const plan = byLevel(difficulty, [
    { count: 3, arrangement: 'line', prompt: 'count_along', target: undefined, distractors: 0 },
    { count: 5, arrangement: 'line', prompt: 'how_many', target: undefined, distractors: 0 },
    { count: 10, arrangement: 'grid', prompt: 'how_many', target: undefined, distractors: 0 },
    { count: 15, arrangement: 'scattered', prompt: 'how_many', target: undefined, distractors: 0 },
    { count: 20, arrangement: 'scattered', prompt: 'give_me_n', target: 12, distractors: 4 },
  ] as const)

  const distractor = pack.items.find((item) => item.emoji !== pack.objectEmoji)

  return CountingGameConfigSchema.parse({
    objectType: pack.objectNoun,
    objectEmoji: pack.objectEmoji,
    count: plan.count,
    arrangement: plan.arrangement,
    gridColumns: 5,
    // Pointing and saying the number stay locked together at every level.
    enforceOneToOne: true,
    allowRecount: true,
    tapFeedback: 'both',
    promptStyle: plan.prompt,
    ...(plan.target === undefined ? {} : { targetNumber: plan.target }),
    distractorCount: distractor ? plan.distractors : 0,
    ...(distractor && plan.distractors > 0 ? { distractorEmoji: distractor.emoji } : {}),
    scatterSeed: 2_000 + difficulty,
  })
}

// ─────────────────────────────────────────────────────────────
// pattern_builder
// ─────────────────────────────────────────────────────────────

function patternConfig(difficulty: Level, pack: ThemePack): PatternBuilderConfig {
  const plan = byLevel(difficulty, [
    { type: 'AB', unit: undefined, length: 8, mode: 'continue', reveal: 4, elements: 2 },
    { type: 'AAB', unit: undefined, length: 9, mode: 'continue', reveal: 6, elements: 2 },
    { type: 'ABC', unit: undefined, length: 9, mode: 'continue', reveal: 6, elements: 3 },
    { type: 'AABB', unit: undefined, length: 12, mode: 'continue', reveal: 8, elements: 2 },
    { type: 'custom', unit: [0, 1, 1, 2], length: 12, mode: 'create', reveal: undefined, elements: 3 },
  ] as const)

  return PatternBuilderConfigSchema.parse({
    patternType: plan.type,
    ...(plan.unit ? { customUnit: plan.unit } : {}),
    elements: pack.items.slice(0, plan.elements).map((item) => ({
      id: item.id,
      label: item.label,
      emoji: item.emoji,
    })),
    sequenceLength: plan.length,
    mode: plan.mode,
    ...(plan.reveal === undefined ? {} : { revealCount: plan.reveal }),
    choiceCount: 3,
    chantAloud: true,
  })
}

// ─────────────────────────────────────────────────────────────
// deduction_puzzle
// ─────────────────────────────────────────────────────────────

function deductionAttributes(item: ThemeItem): Record<string, string> {
  return { group: item.group, size: item.size, home: item.home }
}

function deductionConfig(difficulty: Level, pack: ThemePack): DeductionPuzzleConfig {
  // A shorter attribute order is a harder puzzle: what it leaves out has to be
  // settled by negation, which is the half she is still growing into.
  const plan = byLevel(difficulty, [
    { candidates: 3, order: ['group', 'size', 'home'] },
    { candidates: 4, order: ['group', 'size', 'home'] },
    { candidates: 4, order: ['group', 'size'] },
    { candidates: 5, order: ['group'] },
    { candidates: 6, order: ['size'] },
  ] as const)

  const candidates = pack.items.slice(0, plan.candidates).map((item) => ({
    id: item.id,
    label: item.label,
    emoji: item.emoji,
    attributes: deductionAttributes(item),
  }))
  // Deterministic pick, and never the first candidate — the first guess should
  // not be the answer.
  const solution = candidates[candidates.length - 1]
  const clues = buildDeductionPuzzle(candidates, solution.id, plan.order)

  return DeductionPuzzleConfigSchema.parse({
    question: 'I am thinking of one of these. Which one is it?',
    candidates,
    clues,
    solutionId: solution.id,
    revealMode: 'one_at_a_time',
    allowElimination: true,
    celebrateAttempts: true,
    rereadHint: true,
  })
}

// ─────────────────────────────────────────────────────────────
// sorting_game
// ─────────────────────────────────────────────────────────────

function sortingConfig(difficulty: Level, pack: ThemePack): SortingGameConfig {
  const plan = byLevel(difficulty, [
    { items: 4, criteria: ['group'] },
    { items: 6, criteria: ['group'] },
    { items: 6, criteria: ['group'] },
    { items: 6, criteria: ['group', 'size'] },
    { items: pack.items.length, criteria: ['group', 'size'] },
  ] as const) as { items: number; criteria: readonly string[] }

  const chosen = pack.items.slice(0, plan.items)
  const items = chosen.map((item) => ({
    id: item.id,
    label: item.label,
    emoji: item.emoji,
    attributes: deductionAttributes(item),
  }))

  // Bins are derived from what is actually in the hunt, so none is ever empty.
  const bins: SortingGameConfig['bins'] = []
  const seen = new Set<string>()
  for (const item of items) {
    const values = plan.criteria.map((criterion) => item.attributes[criterion])
    const key = values.join('|')
    if (seen.has(key)) continue
    seen.add(key)
    bins.push({
      id: `bin-${values.join('-').replace(/\s+/g, '-')}`,
      label: `${values.join(' ')} ${pack.binNoun}`,
      match: Object.fromEntries(plan.criteria.map((criterion, index) => [criterion, values[index]])),
    })
  }

  return SortingGameConfigSchema.parse({
    // Never an abstract sort. The job in the story is what made this work.
    framing: {
      scenario: pack.huntScenario,
      storyPrompt: pack.huntPrompt,
      huntVerb: pack.huntVerb,
      binNoun: pack.binNoun,
    },
    items,
    criteria: plan.criteria,
    bins,
    revealItemsOneAtATime: true,
    shuffleSeed: 3_000 + difficulty,
    gentleCorrection: true,
  })
}

// ─────────────────────────────────────────────────────────────
// sequence_order
// ─────────────────────────────────────────────────────────────

function sequenceConfig(difficulty: Level, pack: ThemePack): SequenceOrderConfig {
  const wanted = byLevel(difficulty, [3, 4, 5, 6, 7] as const)
  const all = pack.sequence.steps
  // Keep the real ending: a story that stops halfway is not a sequence.
  const steps = wanted >= all.length ? all : [...all.slice(0, wanted - 1), all[all.length - 1]]

  return SequenceOrderConfigSchema.parse({
    storyTitle: pack.sequence.title,
    steps: steps.map((step) => ({ id: step.id, label: step.label, emoji: step.emoji })),
    correctOrder: steps.map((step) => step.id),
    shuffleSeed: 4_000 + difficulty,
    presentation: 'horizontal',
    showNumbers: difficulty <= 2,
    narrationPrompt: 'Tell me what happens first, and what happens after that.',
  })
}

// ─────────────────────────────────────────────────────────────
// letter_tracing
// ─────────────────────────────────────────────────────────────

const LETTER_KEYWORDS: Record<string, string> = {
  A: 'A is for apple', C: 'C is for cat', E: 'E is for egg', H: 'H is for hat',
  I: 'I is for igloo', L: 'L is for leaf', O: 'O is for owl', R: 'R is for rainbow',
  S: 'S is for sun', T: 'T is for tree', U: 'U is for umbrella', X: 'X is for box',
  a: 'a is for ant', c: 'c is for cat', d: 'd is for dog', o: 'o is for owl',
  s: 's is for sun', v: 'v is for van', w: 'w is for wave', x: 'x is for box',
  z: 'z is for zip',
}

function tracingConfig(difficulty: Level, pack: ThemePack): LetterTracingConfig {
  const plan = byLevel(difficulty, [
    { chars: UPPERCASE_ORDER.slice(0, 2), guide: 'full', order: false, tolerance: 0.18, fuse: false },
    { chars: UPPERCASE_ORDER.slice(0, 3), guide: 'dotted', order: false, tolerance: 0.16, fuse: false },
    { chars: UPPERCASE_ORDER.slice(0, 4), guide: 'dotted', order: true, tolerance: 0.14, fuse: true },
    { chars: LOWERCASE_FORMATION_ORDER.slice(0, 4), guide: 'start_dot', order: true, tolerance: 0.12, fuse: true },
    { chars: LOWERCASE_FORMATION_ORDER.slice(0, 5), guide: 'none', order: true, tolerance: 0.1, fuse: true },
  ] as const) as {
    chars: readonly string[]
    guide: 'full' | 'dotted' | 'start_dot' | 'none'
    order: boolean
    tolerance: number
    fuse: boolean
  }

  const letters = plan.chars.map((char) => {
    const template = strokeTemplate(char)
    if (!template) throw new Error(`letter_tracing: no stroke path for "${char}"`)
    return {
      char: template.char,
      letterCase: template.letterCase,
      strokes: template.strokes,
      ...(LETTER_KEYWORDS[char] ? { keyword: LETTER_KEYWORDS[char] } : {}),
    }
  })

  return LetterTracingConfigSchema.parse({
    letters,
    enforceStrokeOrder: plan.order,
    // Direction is the habit that transfers, so it holds at every level.
    enforceDirection: true,
    guide: plan.guide,
    toleranceRadius: plan.tolerance,
    showLetterName: true,
    // Isolated drills get skipped, so the writing lives inside a drawing.
    fuseWithDrawing: plan.fuse
      ? { enabled: true, prompt: `Draw a ${pack.objectNoun} and write its first letter underneath.` }
      : { enabled: false },
    acceptInventedSpelling: true,
  })
}

// ─────────────────────────────────────────────────────────────
// reading_timer
// ─────────────────────────────────────────────────────────────

function readingTimerConfig(difficulty: Level, pack: ThemePack): ReadingTimerConfig {
  const useWordList = difficulty <= 2
  const passageLevel = (difficulty <= 3 ? 3 : difficulty) as 3 | 4 | 5
  const wordCount = useWordList ? (difficulty === 1 ? 8 : 12) : countWords(READING_PASSAGES[passageLevel])
  // Honest seconds per word at each level, so the target is beatable today.
  const secondsPerWord = byLevel(difficulty, [2.5, 2, 1.8, 1.5, 1.2] as const)

  return ReadingTimerConfigSchema.parse({
    title: useWordList ? 'Say them fast' : 'Beat your time',
    content: useWordList
      ? { mode: 'word_list', words: SIGHT_WORDS.slice(0, wordCount) }
      : { mode: 'passage', text: READING_PASSAGES[passageLevel], wordCount },
    targetSeconds: Math.round(wordCount * secondsPerWord),
    previousTimesSeconds: [],
    attemptsThisSession: 3,
    // Reading to someone is the engine — her caregiving instinct doubles the volume.
    readToAudience: { enabled: true, audience: pack.audience },
    celebrateImprovementOnly: true,
    showLiveTimer: false,
    coachNote: 'Read it again and see how smooth it gets.',
  })
}

// ─────────────────────────────────────────────────────────────
// memory_pairs
// ─────────────────────────────────────────────────────────────

function memoryPool(pack: ThemePack): Array<{ label: string; emoji: string }> {
  const pool: Array<{ label: string; emoji: string }> = []
  const seen = new Set<string>()
  for (const entry of [...pack.items, ...pack.characters, ...pack.settings]) {
    if (seen.has(entry.label)) continue
    seen.add(entry.label)
    pool.push({ label: entry.label, emoji: entry.emoji })
  }
  return pool
}

function memoryConfig(difficulty: Level, pack: ThemePack): MemoryPairsConfig {
  // Level 5 is fewer cards but harder work: the word matched to the picture is
  // where memory starts feeding reading.
  const plan = byLevel(difficulty, [
    { pairs: 4, columns: 4, rows: 2, kind: 'identical', revealMs: 2_000 },
    { pairs: 6, columns: 4, rows: 3, kind: 'identical', revealMs: 1_600 },
    { pairs: 8, columns: 4, rows: 4, kind: 'identical', revealMs: 1_400 },
    { pairs: 10, columns: 5, rows: 4, kind: 'identical', revealMs: 1_200 },
    { pairs: 6, columns: 4, rows: 3, kind: 'associated', revealMs: 1_400 },
  ] as const)

  // Associated pairs ask her to read the card, so they use the short nouns only.
  const source = plan.kind === 'associated'
    ? pack.items.map((item) => ({ label: item.label, emoji: item.emoji }))
    : memoryPool(pack)
  if (source.length < plan.pairs) {
    throw new Error(`memory_pairs: theme "${pack.key}" has ${source.length} cards, needs ${plan.pairs}`)
  }

  const pairs = source.slice(0, plan.pairs).map((face, index) => ({
    id: `pair-${index + 1}`,
    a: plan.kind === 'associated' ? { label: face.label } : { label: face.label, emoji: face.emoji },
    b: { label: face.label, emoji: face.emoji },
  }))

  return MemoryPairsConfigSchema.parse({
    pairCount: plan.pairs,
    pairKind: plan.kind,
    pairs,
    layout: { columns: plan.columns, rows: plan.rows },
    faceDownRevealMs: plan.revealMs,
    shuffleSeed: 5_000 + difficulty,
    nameCardsAloud: true,
  })
}

// ─────────────────────────────────────────────────────────────
// model_3d
// ─────────────────────────────────────────────────────────────

function modelConfig(difficulty: Level): Model3dConfig {
  switch (difficulty) {
    case 1:
    case 2:
      return Model3dConfigSchema.parse({
        modelType: 'earth_sun_day_night',
        interactions: ['rotate', 'play_pause'],
        labels: [
          { id: 'l-sun', partKey: 'sun', text: 'the Sun', visibleByDefault: true },
          { id: 'l-earth', partKey: 'earth', text: 'Earth', visibleByDefault: true },
        ],
        cameraPreset: 'front',
        autoRotate: true,
        timeScale: 1,
        narrationPrompt: 'Turn it slowly. Where is it daytime, and where is it night?',
      })
    case 3:
      return Model3dConfigSchema.parse({
        modelType: 'earth_sun_day_night',
        interactions: ['rotate', 'zoom', 'play_pause', 'scrub_time', 'toggle_labels'],
        labels: [
          { id: 'l-sun', partKey: 'sun', text: 'the Sun', visibleByDefault: true },
          { id: 'l-earth', partKey: 'earth', text: 'Earth', visibleByDefault: true },
          { id: 'l-day', partKey: 'day_side', text: 'daytime side', visibleByDefault: true },
          { id: 'l-night', partKey: 'night_side', text: 'night side', visibleByDefault: true },
        ],
        cameraPreset: 'orbit',
        autoRotate: false,
        timeScale: 2,
        narrationPrompt: 'Wind the day forward. What happens to your house as it turns?',
      })
    case 4:
      return Model3dConfigSchema.parse({
        modelType: 'solar_system',
        interactions: ['rotate', 'zoom', 'play_pause', 'tap_parts', 'toggle_labels'],
        planetsShown: ['mercury', 'venus', 'earth', 'mars'],
        labels: [
          { id: 'l-sun', partKey: 'sun', text: 'the Sun', visibleByDefault: true },
          { id: 'l-earth', partKey: 'earth', text: 'Earth', visibleByDefault: true },
          { id: 'l-mars', partKey: 'mars', text: 'Mars', visibleByDefault: false },
        ],
        cameraPreset: 'top',
        autoRotate: true,
        timeScale: 4,
        narrationPrompt: 'Which one travels the longest way around?',
      })
    default:
      return Model3dConfigSchema.parse({
        modelType: 'body_systems',
        bodySystem: 'heart_lungs',
        interactions: ['rotate', 'zoom', 'tap_parts', 'toggle_labels'],
        labels: [
          { id: 'l-heart', partKey: 'heart', text: 'heart', visibleByDefault: true },
          { id: 'l-lungs', partKey: 'lungs', text: 'lungs', visibleByDefault: true },
        ],
        cameraPreset: 'close',
        autoRotate: false,
        timeScale: 1,
        narrationPrompt: 'Put your hand on your chest. Can you feel it doing that?',
      })
  }
}

// ─────────────────────────────────────────────────────────────
// story_builder
// ─────────────────────────────────────────────────────────────

function storyConfig(difficulty: Level, pack: ThemePack): StoryBuilderConfig {
  const plan = byLevel(difficulty, [
    { pages: 3, entry: false, mode: 'none', style: 'guided' },
    { pages: 3, entry: true, mode: 'dictation', style: 'guided' },
    { pages: 4, entry: true, mode: 'dictation', style: 'guided' },
    { pages: 5, entry: true, mode: 'invented_spelling', style: 'guided' },
    { pages: 6, entry: true, mode: 'invented_spelling', style: 'free' },
  ] as const)

  const choices = (entries: ThemePack['characters']) =>
    entries.map((entry) => ({ id: entry.id, label: entry.label, emoji: entry.emoji }))

  return StoryBuilderConfigSchema.parse({
    characters: choices(pack.characters),
    settings: choices(pack.settings),
    problems: choices(pack.problems),
    resolutions: choices(pack.resolutions),
    pageCount: plan.pages,
    textEntry: { enabled: plan.entry, mode: plan.mode, neverCorrectSpelling: true },
    drawingEnabled: true,
    exportToBook: true,
    promptStyle: plan.style,
    readAloudTo: pack.audience,
    coverPrompt: 'Draw the cover, and write your name as the author.',
  })
}

// ─────────────────────────────────────────────────────────────
// entry points
// ─────────────────────────────────────────────────────────────

const FACTORIES: {
  [K in InteractiveKind]: (difficulty: Level, pack: ThemePack) => ConfigByKind[K]
} = {
  tangram: (difficulty) => tangramConfig(difficulty),
  matrix_reasoning: matrixConfig,
  sight_word_deck: (difficulty) => sightWordConfig(difficulty),
  counting_game: countingConfig,
  pattern_builder: patternConfig,
  deduction_puzzle: deductionConfig,
  sorting_game: sortingConfig,
  sequence_order: sequenceConfig,
  letter_tracing: tracingConfig,
  reading_timer: readingTimerConfig,
  memory_pairs: memoryConfig,
  model_3d: (difficulty) => modelConfig(difficulty),
  story_builder: storyConfig,
}

/**
 * A complete, valid config for any kind at any level.
 *
 * `theme` is a free-text interest topic. An unknown topic falls back to the
 * everyday pack rather than failing — the activity still runs.
 */
export function defaultConfigFor<K extends InteractiveKind>(
  kind: K,
  difficulty: Level,
  theme?: string,
): ConfigByKind[K] {
  return FACTORIES[kind](difficulty, resolveThemePack(theme))
}

export interface DefaultSpecOptions {
  theme?: string
  targetSkillIds?: readonly SkillId[]
  autoLogObservation?: boolean
}

/** The whole spec, ready to preview, attach to a block, or hand to the agent to edit. */
export function defaultSpecFor<K extends InteractiveKind>(
  kind: K,
  difficulty: Level,
  options: DefaultSpecOptions = {},
): InteractiveSpecBase & { kind: K; config: ConfigByKind[K] } {
  const pack = resolveThemePack(options.theme)
  const themed = options.theme && pack.key !== 'everyday'
  const targetSkillIds = [...(options.targetSkillIds ?? [])]
  return {
    kind,
    title: themed ? `${pack.label} ${KIND_LABELS[kind]}` : KIND_LABELS[kind],
    difficulty,
    ...(options.theme ? { theme: options.theme } : {}),
    targetSkillIds,
    successCriteria: {
      // A draft with no skills yet cannot log an observation, so it says so
      // rather than producing a spec that fails validation.
      autoLogObservation: options.autoLogObservation ?? targetSkillIds.length > 0,
    },
    config: FACTORIES[kind](difficulty, pack),
  }
}
